import { useId } from "react";
import { Activity } from "lucide-react";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent } from "../../ui/card";
import { AlertsTimelineData, Skeleton } from "../interfaces/CommonInterfaces";

export default function AlertOverview({
  alertsline,
  loading,
  fromdetails = false,
}: {
  alertsline: AlertsTimelineData[];
  loading: boolean;
  fromdetails?: boolean;
}) {
  const gradientId = `endpoint-trend-${useId().replace(/:/g, "")}`;

  return (
    <Card className="overview-card overview-timeline-card h-full lg:col-span-2">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Alerts Overview"
        description="Alert volume over the selected time range"
        icon={Activity}
      />
      <CardContent className="overview-card-content">
        <div className="overview-timeline flex items-center justify-center h-64">
          {loading ? (
            // Skeleton placeholder
            <Skeleton className="w-full h-64 rounded-md" />
          ) : alertsline && alertsline.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={alertsline}
                margin={
                  fromdetails
                    ? { top: 12, right: 12, left: -20, bottom: 0 }
                    : undefined
                }
              >
                {fromdetails && (
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0.22}
                      />
                      <stop
                        offset="100%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                )}
                <CartesianGrid
                  strokeDasharray={fromdetails ? "4 6" : "3 3"}
                  vertical={fromdetails ? false : undefined}
                  stroke={fromdetails ? "hsl(var(--border))" : undefined}
                  className="opacity-30"
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: fromdetails ? 11 : 12,
                    ...(fromdetails && {
                      fill: "hsl(var(--muted-foreground))",
                    }),
                  }}
                  className="text-muted-foreground"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: fromdetails ? 11 : 12,
                    ...(fromdetails && {
                      fill: "hsl(var(--muted-foreground))",
                    }),
                  }}
                  className="text-muted-foreground"
                />
                <Tooltip
                  wrapperClassName={
                    fromdetails ? "overview-chart-tooltip" : undefined
                  }
                  labelFormatter={(label) => `Date: ${label}`}
                  formatter={(value) => [value, "Alerts"]}
                />
                <Area
                  type="monotone"
                  dataKey="alerts"
                  stroke="hsl(var(--primary))"
                  fill={
                    fromdetails
                      ? `url(#${gradientId})`
                      : "hsl(var(--primary) / 0.2)"
                  }
                  strokeWidth={fromdetails ? 2.5 : 2}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : fromdetails ? (
            <OverviewEmptyState
              icon={Activity}
              message="No alerts found in this time range."
            />
          ) : (
            <div className="w-full text-sm text-center text-muted-foreground">
              No data found.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
